/**
 * OmegaCeoDashboardPage — Phase 36.7a.
 *
 * Founder morning dashboard: top decisions + today's briefing narrative.
 * Decisions poll every 20 s; briefing polls every 5 min. English-only.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface BriefingDetailResponse {
  briefing_id: string;
  generated_at: number;
  narrative: string;
  context_snapshot: string;
  status: string;
  narrative_length: number;
}

interface DecisionItem {
  item_id: string;
  source_brain: string;
  item_type: string;
  priority: 'P0' | 'P1' | 'P2' | 'P3';
  age_seconds: number;
  summary: string;
  action_url: string;
  rank_score: number;
}

interface DecisionQueueResponse {
  items: DecisionItem[];
  total_considered: number;
  limit_applied: number;
}

// ── Helpers (exported for tests) ─────────────────────────────────────────────

export function fmtAge(ageSeconds: number): string {
  if (ageSeconds < 3600)  return `${Math.floor(ageSeconds / 60)}m ago`;
  if (ageSeconds < 86400) return `${Math.floor(ageSeconds / 3600)}h ago`;
  return `${Math.floor(ageSeconds / 86400)}d ago`;
}

export function brainLabel(brain: string): string {
  const LABELS: Record<string, string> = {
    marketing:   'Marketing',
    sales:       'Sales',
    customer:    'Customers',
    finance:     'Finance',
    alpha_omega: 'ALPHA-OMEGA',
  };
  return LABELS[brain] ?? brain;
}

export function fmtBriefingDate(unixSecs: number): string {
  // Founder dashboard is English-only.
  return new Date(unixSecs * 1000).toLocaleString('en-US', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export const PRIORITY_COLORS: Record<string, string> = {
  P0: '#ef4444',
  P1: '#f97316',
  P2: '#fbbf24',
  P3: '#6b7280',
};

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchBriefingLatest(): Promise<BriefingDetailResponse> {
  return apiFetch<BriefingDetailResponse>('/internal/ceo/daily-briefing/latest');
}

async function fetchDecisionQueue(): Promise<DecisionQueueResponse> {
  return apiFetch<DecisionQueueResponse>('/internal/ceo/decision-queue?limit=50');
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, highlight }: {
  label: string; value: string | number; sub?: string; color?: string; highlight?: boolean;
}) {
  return (
    <div style={{
      background: highlight ? 'rgba(125,211,252,0.08)' : 'var(--omega-surface, rgba(255,255,255,0.04))',
      border: highlight ? '1px solid rgba(125,211,252,0.4)' : '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
      borderRadius: 6, padding: '14px 18px', minWidth: 140,
    }}>
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>{value}</div>
      {sub && <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const navLinkStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
  borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt', fontWeight: 600, padding: '6px 14px',
  textDecoration: 'none', letterSpacing: '0.04em',
};

// ── Page ─────────────────────────────────────────────────────────────────────

export function OmegaCeoDashboardPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data: briefingData } = useQuery({
    queryKey: ['ceo', 'briefing', 'latest'],
    queryFn: fetchBriefingLatest,
    refetchInterval: 300_000,
  });

  const { data: decisionsData, isPending: decisionsPending, isError: decisionsError } = useQuery({
    queryKey: ['ceo', 'decision-queue'],
    queryFn: fetchDecisionQueue,
    refetchInterval: 20_000,
  });

  const topDecisions = decisionsData?.items.slice(0, 5) ?? [];
  const briefingAge = briefingData
    ? fmtAge(Math.floor(Date.now() / 1000) - briefingData.generated_at)
    : null;

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'CEO' }]} />

      <div style={{ marginBottom: 20, marginTop: 8, direction: 'ltr' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          CEO Dashboard
        </h1>
        <div style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)', marginTop: 4, fontFamily: 'var(--omega-mono)' }}>
          Your GI is ready
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
        <StatCard
          label="Pending Decisions"
          value={decisionsData ? decisionsData.items.length : '—'}
          color={decisionsData && decisionsData.items.length > 0 ? '#fbbf24' : 'var(--omega-ink)'}
          highlight={!!decisionsData && decisionsData.items.length > 0}
        />
        {briefingData ? (
          <StatCard
            label="Daily Briefing"
            value={briefingAge ?? '—'}
            sub={fmtBriefingDate(briefingData.generated_at)}
          />
        ) : (
          <StatCard
            label="Daily Briefing"
            value="—"
          />
        )}
      </div>

      {/* Nav links */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 28 }}>
        <Link to="/omega/ceo/briefings" style={navLinkStyle}>Briefings</Link>
        <Link to="/omega/ceo/decisions" style={navLinkStyle}>Decision Queue</Link>
      </div>

      {/* Decisions section */}
      <section style={{ marginBottom: 32 }}>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
          What needs your attention
        </div>

        {decisionsPending && (
          <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>
        )}
        {decisionsError && (
          <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load the decision queue.</div>
        )}
        {!decisionsPending && !decisionsError && topDecisions.length === 0 && (
          <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', direction: 'ltr' }}>
            No pending items — all clear ✓
          </div>
        )}

        {topDecisions.map(item => (
          <div
            key={item.item_id}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '10px 12px',
              borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
              borderLeft: item.priority === 'P0' || item.priority === 'P1'
                ? '3px solid #ef4444'
                : '3px solid transparent',
              direction: 'ltr',
            }}
          >
            <span style={{
              fontSize: '7.5pt', fontWeight: 700, padding: '2px 7px',
              border: `1px solid ${PRIORITY_COLORS[item.priority] ?? '#6b7280'}`,
              color: PRIORITY_COLORS[item.priority] ?? '#6b7280',
              borderRadius: 3, letterSpacing: '0.06em',
              textTransform: 'uppercase', whiteSpace: 'nowrap',
            }}>
              {item.priority}
            </span>
            <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', whiteSpace: 'nowrap' }}>
              {brainLabel(item.source_brain)}
            </span>
            <span style={{ fontSize: '9.5pt', flex: 1, direction: 'ltr' }}>
              {item.summary}
            </span>
            <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', whiteSpace: 'nowrap' }}>
              {fmtAge(item.age_seconds)}
            </span>
            <a
              href={item.action_url}
              style={{
                background: 'rgba(251,191,36,0.12)',
                border: '1px solid rgba(251,191,36,0.35)',
                borderRadius: 4, color: '#fbbf24',
                fontFamily: 'var(--omega-mono)', fontSize: '8pt', fontWeight: 600,
                padding: '4px 10px', textDecoration: 'none', whiteSpace: 'nowrap',
              }}
            >
              Handle →
            </a>
          </div>
        ))}
      </section>

      {/* Briefing section */}
      <section>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
          Daily Briefing
        </div>

        {briefingData ? (
          <div style={{
            background: 'var(--omega-surface, rgba(255,255,255,0.04))',
            border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
            borderRadius: 6, padding: '16px 18px',
          }}>
            <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', marginBottom: 10, direction: 'ltr' }}>
              {fmtBriefingDate(briefingData.generated_at)}
            </div>
            <pre style={{
              margin: 0, fontFamily: 'var(--omega-mono)', fontSize: '9pt',
              color: 'var(--omega-ink)', whiteSpace: 'pre-wrap', direction: 'ltr',
            }}>
              {briefingData.narrative}
            </pre>
          </div>
        ) : (
          <div style={{
            background: 'var(--omega-surface, rgba(255,255,255,0.04))',
            border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
            borderRadius: 6, padding: '16px 18px',
            color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', direction: 'ltr',
          }}>
            No briefing yet today — generated at 06:00
          </div>
        )}
      </section>
    </div>
  );
}
